import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Button,
  CircularProgress,
  Stack,
  Typography,
  Box,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { CameraAlt, Upload, PhotoCamera } from '@mui/icons-material';
import { GameStatus } from '../../../constants/gameConstants';
import UndoButton from './undoButton';
import { saveScorecardPhoto } from '../../../features/game/gameSlice';
import ViewScorecardPhoto from './viewScorecardPhoto';
import { hasWorkspaceAccess, Roles } from '../../../helpers/access';
import { prepareScorecardPhoto } from '../../../helpers/imageResize';

import { useAuth } from '../../../contexts/authContext';

const GameControl = ({ game, isPublic, portal }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const cameraInputRef = useRef(null);
  const uploadInputRef = useRef(null);
  const processingPhotoRef = useRef(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [showPhotoReminder, setShowPhotoReminder] = useState(false);
  const [processingPhoto, setProcessingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState(null);
  const [portalSavingPhoto, setPortalSavingPhoto] = useState(false);
  const open = Boolean(anchorEl);

  const hasSets = game.gameFormat.sets > 1 && game.status !== GameStatus.PLAYOFF;
  const reduxSavingPhoto = useSelector((state) => state.game.savingScorecardPhoto);
  const savingPhoto = portal ? portalSavingPhoto : reduxSavingPhoto;
  const currentSet = game.sets?.find((set) => set.id === game.currentSet);

  const { currentUserProfile, authenticated, currentWorkspace } = useAuth();

  const showPhotoOptions =
    Boolean(portal) ||
    (authenticated && hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.SCORER)) ||
    isPublic;

  // Show photo reminder popup when landing on a complete game without a photo
  useEffect(() => {
    if (game.status === GameStatus.COMPLETE && !game.scorecardPhoto && showPhotoOptions) {
      setShowPhotoReminder(true);
    }
  }, [game.status, game.scorecardPhoto, showPhotoOptions]);

  const savePhoto = (image) => {
    if (portal?.savePhoto) {
      setPortalSavingPhoto(true);
      return portal
        .savePhoto(image)
        .catch((error) => {
          setPhotoError(portal.photoErrorMessage?.(error) || 'The photo could not be saved.');
        })
        .finally(() => setPortalSavingPhoto(false));
    }

    dispatch(saveScorecardPhoto({ gameId: game.id, image }));
    return undefined;
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    setPhotoError(null);

    if (processingPhotoRef.current) return;
    if (!file) return;

    processingPhotoRef.current = true;
    setProcessingPhoto(true);

    prepareScorecardPhoto(file, { isMobile })
      .then(savePhoto)
      .catch((error) => {
        setPhotoError(error?.message || 'Failed to process image. Please try again.');
      })
      .finally(() => {
        processingPhotoRef.current = false;
        setProcessingPhoto(false);
      });
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleTakePhoto = () => {
    handleMenuClose();
    if (cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  };

  const handleUploadPhoto = () => {
    handleMenuClose();
    if (uploadInputRef.current) {
      uploadInputRef.current.click();
    }
  };

  const handleReminderTakePhoto = () => {
    setShowPhotoReminder(false);
    if (cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  };

  const handleReminderUploadPhoto = () => {
    setShowPhotoReminder(false);
    if (uploadInputRef.current) {
      uploadInputRef.current.click();
    }
  };

  const handleReminderCancel = () => {
    setShowPhotoReminder(false);
  };

  return (
    <Stack alignItems={'center'}>
      {game.status === GameStatus.IN_PROGRESS && hasSets && !currentSet?.isDone && (
        <>
          <Typography>{game.gameFormat.setAsSkin ? `Skin` : `Set`}</Typography>
          <Typography>{`${game.sets.length} / ${game.gameFormat.sets}`}</Typography>
        </>
      )}

      {game.status === GameStatus.PLAYOFF && game.playoff && (
        <Typography>{`Tiebreaker: ${game.playoff.ends.length} / ${game.gameFormat.playoffMaxPoints}`}</Typography>
      )}

      {game.status === GameStatus.IN_PROGRESS && !currentSet?.isDone && (
        <>
          <Typography>{`End`}</Typography>
          <Typography>{`${game.currentEnd} / ${game.gameFormat.ends === 0 ? '♾️' : game.gameFormat.ends}`}</Typography>
        </>
      )}

      <UndoButton game={game} isPublic={isPublic} portal={portal} />

      {game.status === GameStatus.COMPLETE ? (
        savingPhoto || processingPhoto ? (
          <CircularProgress color="info" />
        ) : (
          <>
            {/* Camera input - with capture attribute */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            {/* Upload input - without capture attribute */}
            <input
              ref={uploadInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            {photoError && (
              <Typography variant="caption" color="error" textAlign="center">
                {photoError}
              </Typography>
            )}

            {game.scorecardPhoto && <ViewScorecardPhoto game={game} />}

            {showPhotoOptions && (
              <>
                <Button
                  variant={game.scorecardPhoto ? 'text' : 'contained'}
                  size="small"
                  color="info"
                  onClick={handleMenuOpen}
                  startIcon={<PhotoCamera />}>
                  {game.scorecardPhoto ? 'Retake Photo' : 'Add Scorecard Photo'}
                </Button>

                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                  }}
                  transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                  }}>
                  <MenuItem onClick={handleTakePhoto}>
                    <CameraAlt sx={{ mr: 1 }} />
                    Take Photo
                  </MenuItem>
                  <MenuItem onClick={handleUploadPhoto}>
                    <Upload sx={{ mr: 1 }} />
                    Upload Photo
                  </MenuItem>
                </Menu>
              </>
            )}

            {/* Photo Reminder Dialog */}
            <Dialog open={showPhotoReminder} onClose={handleReminderCancel} maxWidth="xs" fullWidth>
              <DialogTitle>Add Scorecard Photo</DialogTitle>
              <DialogContent>
                <Typography>Would you like to add a photo of the scorecard?</Typography>
              </DialogContent>
              <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button variant="outlined" onClick={handleReminderCancel} color="info">
                  Close
                </Button>
                <Box>
                  <Button
                    onClick={handleReminderUploadPhoto}
                    startIcon={<Upload />}
                    color="primary">
                    Upload
                  </Button>
                  <Button
                    onClick={handleReminderTakePhoto}
                    startIcon={<CameraAlt />}
                    color="primary"
                    variant="contained">
                    Take Photo
                  </Button>
                </Box>
              </DialogActions>
            </Dialog>
          </>
        )
      ) : null}
    </Stack>
  );
};

export default GameControl;
